import { useEffect, useRef, useState } from "react";
import axios from "axios";

const BreakingNews = () => {
  const [stories, setStories] = useState([]);
  const [storiesLoaded, setStoriesLoaded] = useState(false);
  const [activeStoryIndex, setActiveStoryIndex] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const storyIntervalRef = useRef(null);

  useEffect(() => {
    const fetchStories = async () => {
      try {
        const baseApi = process.env.REACT_APP_BACKEND_URL;
        const urls = [`${baseApi}story`, `${baseApi}stories`];
        let data = null;

        for (const url of urls) {
          try {
            const res = await axios.get(url);
            if (Array.isArray(res.data)) {
              data = res.data;
              break;
            }
          } catch (err) {
            // try next url
          }
        }

        if (data) {
          const baseUrl = baseApi.replace("/api/v1/", "");
          const resolveImage = (img) => {
            if (!img) return "/no-image.png";
            if (img.startsWith("http") || img.startsWith("/")) return img;
            return `${baseUrl}/uploads/stories/${img}`;
          };

          const mapped = data
            .map((item) => {
              const extraImages = Array.isArray(item.images) ? item.images : [];
              const extraHeadings = Array.isArray(item.image_headings)
                ? item.image_headings
                : [];

              const extraSlides = extraImages.map((img, idx) => ({
                image: resolveImage(img),
                heading: extraHeadings[idx] || item.heading,
              }));

              return {
                id: item.id,
                slides: [
                  {
                    image: resolveImage(item.image),
                    heading: item.heading,
                  },
                  ...extraSlides,
                ].filter((s) => s.heading),
              };
            })
            .filter((item) => item.slides.length > 0);

          setStories(mapped);
        } else {
          setStories([]);
        }
      } catch (err) {
        console.error("Failed to fetch web stories", err);
        setStories([]);
      } finally {
        setStoriesLoaded(true);
      }
    };

    fetchStories();
  }, []);

  useEffect(() => {
    if (activeStoryIndex !== null && activeStoryIndex >= stories.length) {
      closeStory();
    }
  }, [stories.length, activeStoryIndex]);

  const shorten = (text, max = 50) => {
    if (!text) return "";
    return text.length > max ? `${text.slice(0, max)}...` : text;
  };

  const headlineItems = stories
    .flatMap((story, storyIndex) =>
      story.slides.map((slide, slideIndex) => ({
        storyIndex,
        slideIndex,
        heading: slide.heading,
      }))
    )
    .filter((item) => item.heading)
    .slice(0, 12);

  const marqueeItems = headlineItems.length
    ? [...headlineItems, ...headlineItems]
    : [];

  const openStoryAt = (storyIndex, slideIndex) => {
    setActiveStoryIndex(storyIndex);
    setCurrentSlide(slideIndex);
    setIsPaused(false);
  };

  const closeStory = () => {
    setActiveStoryIndex(null);
    setCurrentSlide(0);
    setIsPaused(false);
  };

  const activeStory =
    activeStoryIndex !== null ? stories[activeStoryIndex] : null;

  useEffect(() => {
    if (!activeStory || isPaused) return;

    storyIntervalRef.current = setInterval(() => {
      setCurrentSlide((prev) => {
        if (prev < activeStory.slides.length - 1) {
          return prev + 1;
        } else {
          if (activeStoryIndex < stories.length - 1) {
            setActiveStoryIndex((prevIndex) => prevIndex + 1);
            return 0;
          } else {
            closeStory();
            return prev;
          }
        }
      });
    }, 3000);

    return () => clearInterval(storyIntervalRef.current);
  }, [activeStoryIndex, isPaused, stories.length]);

  const nextSlide = () => {
    if (!activeStory) return;
    if (currentSlide < activeStory.slides.length - 1) {
      setCurrentSlide((prev) => prev + 1);
    } else {
      if (activeStoryIndex < stories.length - 1) {
        setActiveStoryIndex((prev) => prev + 1);
        setCurrentSlide(0);
      } else {
        closeStory();
      }
    }
  };

  const prevSlide = () => {
    if (!activeStory) return;
    if (currentSlide > 0) {
      setCurrentSlide((prev) => prev - 1);
    } else if (activeStoryIndex > 0) {
      const previousStory = stories[activeStoryIndex - 1];
      setActiveStoryIndex((prev) => prev - 1);
      setCurrentSlide(previousStory.slides.length - 1);
    }
  };

  return (
    <>
      <div className="hidden sm:flex bg-red-600 text-white -mt-10 px-4 py-2 rounded gap-3 sm:mt-5 items-center">
        <span className="font-bold whitespace-nowrap">ब्रेकिंग न्यूज़</span>
        <div className="relative flex-1 overflow-hidden">
          <div className="whitespace-nowrap animate-marquee text-sm">
            {marqueeItems.length > 0 ? (
              marqueeItems.map((item, index) => (
                <button
                  key={`${item.storyIndex}-${item.slideIndex}-${index}`}
                  onClick={() => openStoryAt(item.storyIndex, item.slideIndex)}
                  className="hover:underline underline-offset-4"
                >
                  {shorten(item.heading)}
                  {index < marqueeItems.length - 1 && (
                    <span className="mx-4">|</span>
                  )}
                </button>
              ))
            ) : (
              <span>
                {storiesLoaded ? "कोई वेब स्टोरी उपलब्ध नहीं है" : "लोड हो रहा है..."}
              </span>
            )}
          </div>
        </div>
      </div>

      {activeStory && (
        <div className="fixed inset-0 bg-black z-[999] flex items-center justify-center">
          <div className="relative w-full h-[100dvh] sm:h-[95vh] sm:max-w-md sm:rounded-xl overflow-hidden bg-black">
            <div className="absolute top-4 left-3 right-3 flex gap-2 z-20">
              {activeStory.slides.map((_, index) => (
                <div
                  key={index}
                  className="flex-1 h-1 bg-gray-600/70 rounded overflow-hidden"
                >
                  <div
                    className={`h-full bg-white ${
                      index === currentSlide && !isPaused
                        ? "animate-progress"
                        : index < currentSlide
                        ? "w-full"
                        : "w-0"
                    }`}
                    style={{
                      animationDuration: "3000ms",
                      animationFillMode: "forwards",
                    }}
                  ></div>
                </div>
              ))}
            </div>

            <div className="absolute top-10 left-4 right-4 flex justify-between items-center text-white z-20">
              <div className="flex items-center gap-4">
                <button onClick={closeStory} className="text-xl sm:text-2xl">
                  <i className="fa-solid fa-xmark"></i>
                </button>

                <button
                  onClick={() => setIsPaused(!isPaused)}
                  className="text-lg sm:text-xl"
                >
                  {isPaused ? (
                    <i className="fa-solid fa-play"></i>
                  ) : (
                    <i className="fa-solid fa-pause"></i>
                  )}
                </button>
              </div>
            </div>

            <img
              key={currentSlide}
              src={activeStory.slides[currentSlide].image}
              alt=""
              className="w-full h-full object-cover"
            />

            <div className="absolute bottom-6 sm:bottom-8 left-4 right-4 text-white bg-black/50 backdrop-blur-md p-4 rounded-lg">
              <h2 className="text-base sm:text-lg md:text-xl text-center font-semibold">
                {activeStory.slides[currentSlide].heading}
              </h2>
            </div>

            <button
              onClick={prevSlide}
              className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 text-white text-2xl sm:text-3xl z-20"
            >
              <i className="fa-solid fa-chevron-left"></i>
            </button>

            <button
              onClick={nextSlide}
              className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 text-white text-2xl sm:text-3xl z-20"
            >
              <i className="fa-solid fa-chevron-right"></i>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default BreakingNews;
